import { ColumnHelper } from "@tanstack/react-table";
import { AccessorFnColumnDef } from "@tanstack/react-table";
import { Table as TanstackTable } from "@tanstack/react-table";
import filter from "lodash/filter";
import flatten from "lodash/flatten";
import values from "lodash/values";
import { TemplateKey } from "PFApp/constants/templates";
import { getFieldTitle } from "PFApp/helpers/activity_form/multi_roles_form/get_field_title";
import { useInterestQuestionariesTemplateConfiguration, useTemplate } from "PFApp/hooks";
import { useFormSectionsGenerator } from "PFApp/hooks/activity_form/use_form_sections_generator";
import { useFormValidation } from "PFApp/hooks/activity_form/use_form_validation";
import { useMultiActivityFormStore } from "PFApp/stores/activity_form/multi_roles_form";
import { FormSchemaField, RoleFormKey, RolesFormKey } from "PFApp/types/activity_form/activity_form_schema";
import { Inline } from "PFComponents/containers";
import { Label } from "PFComponents/label";
import { useCustomTypes } from "PFCore/helpers/use_custom_types";
import { Subtemplate } from "PFCore/types";
import { RefObject, useCallback, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";

import { TabularFieldFactory } from "../parts/tabular_field_factory";
import css from "../tabular_roles_form.module.scss";
import {
  calculateColumnWidth,
  getRoleFormKeyPrefix,
  serializeValue,
  TABLE_COLUMNS_BLACKLIST,
  toFieldFormValue
} from "../tabular_roles_form.utils";
import { useColumnsNames } from "./use_column_names";
import { useGetCellBehavior } from "./use_get_cell_behavior";
import { useHiddenFormControllers } from "./use_hidden_form_controllers";

type UseRolesTableColumnsProps = {
  subtemplate: Subtemplate | null;
  rolesFormKey: RolesFormKey;
  columnsBlacklist?: string[];
  rolesCount: number;
  generateActionsColumn?: (columnHelper: ColumnHelper<any>) => AccessorFnColumnDef<any, any>;
  tableInstanceRef: RefObject<TanstackTable<any> | null>;
  isReadonly?: boolean;
  skipRequiredFieldsValidation?: boolean;
  isEngagementVisible?: boolean;
  isAudit?: boolean;
};

export const useRolesTableColumns = ({
  subtemplate,
  rolesFormKey,
  columnsBlacklist,
  rolesCount,
  generateActionsColumn,
  tableInstanceRef,
  isReadonly = false,
  skipRequiredFieldsValidation,
  isEngagementVisible = false,
  isAudit = false
}: UseRolesTableColumnsProps) => {
  const { control, getValues } = useFormContext();
  const scrollableContainerRef = useMultiActivityFormStore((store) => store.scrollableContainerRef);
  const parentActivity = useMultiActivityFormStore((store) => store.parentActivity);
  const { customTypes } = useCustomTypes();
  const getCellBehavior = useGetCellBehavior(rolesFormKey);
  const roleTemplate = useTemplate(TemplateKey.Role)!;
  const columnNames = useColumnsNames(isAudit);
  const { isFieldRequired } = useFormValidation({
    subtemplate,
    template: roleTemplate,
    skipRequiredFieldsValidation
  });
  const templateConfig = useInterestQuestionariesTemplateConfiguration(roleTemplate);

  const { generateFormSections } = useFormSectionsGenerator({
    isSubtemplateSectionVisible: false,
    template: roleTemplate,
    subtemplate,
    activityCreator: null,
    parentActivity,
    isRole: true,
    isEngagement: false,
    isEngagementVisible,
    isDraft: false,
    isNew: true,
    isQuestionsSectionVisible: !!templateConfig?.showQuestions
  });

  const formSchemaFields = useMemo(() => {
    const formSchema = generateFormSections();
    return filter(
      flatten(values(formSchema)),
      ({ name }) => !TABLE_COLUMNS_BLACKLIST.includes(name) && !columnsBlacklist?.includes(name)
    ) as FormSchemaField[];
  }, [columnsBlacklist, generateFormSections]);

  const hiddenFormControllers = useHiddenFormControllers({
    formSchemaFields,
    rolesCount,
    rolesFormKey,
    subtemplate,
    skipRequiredFieldsValidation
  });

  const fieldPrefixMap = useMemo(
    () =>
      Object.fromEntries(
        formSchemaFields.map((field) => [field.name, getRoleFormKeyPrefix(field)])
      ) as Record<string, string>,
    [formSchemaFields]
  );

  const getFieldPath = useCallback(
    (rowIndex: number, colIndex: number): string | null => {
      const colId = tableInstanceRef.current?.getVisibleLeafColumns()[colIndex]?.id;
      if (!colId) {
        return null;
      }

      const prefix = fieldPrefixMap[colId] ?? "";
      return `${rolesFormKey}.${rowIndex}.${prefix}${colId}`;
    },
    [fieldPrefixMap, rolesFormKey, tableInstanceRef]
  );

  const getFieldByColumnId = useCallback(
    (colId: string): FormSchemaField | undefined => formSchemaFields.find(({ name }) => name === colId),
    [formSchemaFields]
  );

  const generateColumns = useCallback(
    (columnHelper: ColumnHelper<any>) => {
      const columns = formSchemaFields.map((schemaField) => {
        const title = getFieldTitle(schemaField.name, roleTemplate, customTypes, columnNames);
        const required = isFieldRequired(schemaField);
        const prefix = getRoleFormKeyPrefix(schemaField);

        return columnHelper.accessor(
          (row, rowIndex) => `${rolesFormKey}.${rowIndex}.${prefix}${schemaField.name}`,
          {
            id: schemaField.name,
            header: () => (
              <Inline alignItems="center" gap={null} className={css.columnHeader}>
                <Label text={title} required={isReadonly ? false : required} variant="bodyRegular" />
              </Inline>
            ),
            cell: (info) => {
              const roleFormKey: RoleFormKey = `${rolesFormKey}.${info.row.index}`;
              const { disabled, uniqueId: roleUniqueId } = info.row.original;

              return (
                <Controller
                  name={`${roleFormKey}.${prefix}${schemaField.name}`}
                  control={control}
                  // Validation rules are defined in useHiddenFormControllers
                  render={({ field, fieldState }) => (
                    <TabularFieldFactory
                      field={schemaField}
                      value={field.value}
                      onChange={(value) => field.onChange(toFieldFormValue(schemaField, value))}
                      portalRef={scrollableContainerRef}
                      subtemplate={subtemplate}
                      title={title}
                      roleFormKey={roleFormKey}
                      disabled={disabled || isReadonly}
                      error={fieldState.error?.message}
                      rowIndex={info.row.index}
                      colIndex={info.cell.column.getIndex()}
                      roleUniqueId={roleUniqueId}
                      isAudit={isAudit}
                      required={required}
                      roleTemplate={roleTemplate}
                    />
                  )}
                />
              );
            },
            size: calculateColumnWidth(schemaField, title, required),
            meta: {
              getCopyValue: (fieldPath: string) => serializeValue(schemaField, fieldPath, getValues),
              getIsCopyPasteBlocked: (rowOriginal: { uniqueId: string }) =>
                getCellBehavior(rowOriginal, schemaField).isCopyPasteBlocked
            }
          }
        );
      });
      if (generateActionsColumn) {
        columns.push(generateActionsColumn(columnHelper));
      }
      return columns;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      columnNames,
      control,
      customTypes,
      rolesFormKey,
      formSchemaFields,
      isFieldRequired,
      isReadonly,
      scrollableContainerRef,
      roleTemplate,
      subtemplate,
      parentActivity,
      rolesCount, // we need to refresh columns when roles where added/removed
      getCellBehavior
    ]
  );

  return {
    generateColumns,
    hiddenFormControllers,
    getFieldPath,
    getFieldByColumnId,
    getCellBehavior,
    formSchemaFields,
    isFieldRequired
  };
};
